import { isPlatformBrowser } from '@angular/common';
import { inject, PLATFORM_ID } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthService, AuthState, DialogService } from '@gb-webapp/gb-app';
import { AUTH_ROUTES, includesAuthRoute } from './auth-routes.constants';
import { AuthComponent } from './auth.component';

export const authModalGuard: CanActivateFn = (route) => {
  const platformId = inject(PLATFORM_ID);
  const router = inject(Router);
  const dialogService = inject(DialogService);
  const authService = inject(AuthService);

  // Only run on browser (client-side)
  if (!isPlatformBrowser(platformId)) {
    return true; // Allow component to load on server
  }

  // Check if user is already authenticated
  // If authenticated, redirect to home without opening modal
  if (authService.isAuthenticated()) {
    return router.createUrlTree(['/']);
  }

  // Determine the initial state based on the route
  const url = route.url.map((segment) => segment.path).join('/');
  const initialState = url.includes('sign-in') ? AuthState.SIGN_IN : AuthState.SIGN_UP;
  const targetPath = url.includes('sign-in') ? AUTH_ROUTES.SIGN_IN : AUTH_ROUTES.SIGN_UP;

  // Create UrlTree to home - this redirects synchronously before component loads
  const homeUrlTree: UrlTree = router.createUrlTree(['/']);

  // Navigate to home and handle modal opening after navigation
  router.navigate(['/'], { replaceUrl: true }).then(() => {
    // Restore the auth URL using history API (after navigation completes)
    if (globalThis.history) {
      globalThis.history.replaceState({}, '', targetPath);
    }

    // Open the modal
    dialogService
      .openModal(AuthComponent, {
        cssClass: 'modal-medium',
        modalId: 'authModal',
        props: {
          startUpState: initialState,
          autoRedirectOnSocialLogin: true,
        },
      })
      .subscribe(() => {
        // Navigate back when modal closes
        const browserUrl = globalThis.location?.pathname;
        if (includesAuthRoute(browserUrl)) {
          router.navigate(['/'], { replaceUrl: true });
        }
      });
  });

  // Return UrlTree to redirect to home immediately
  // This prevents the component from loading at all
  return homeUrlTree;
};
